import { Box, Typography } from '@mui/material';
import Badge from '@mui/icons-material/Badge';
import { useShowContext } from 'framework/show';
import { useAuthorization } from 'framework/authorization';
import moment from 'moment';
import { READ_EMPLOYEE_IDENTITY_DOCUMENTS, UPDATE_EMPLOYEE_IDENTITY_DOCUMENTS } from 'domain/shared/employee';
import { ShowSection, FormSection } from 'framework/ui/layout';
import { useFormShowContext } from 'framework/form';
import CardIdentityDocumentListInput from 'domain/people/employee/shared/input/IdentityDocumentInput/CardIdentityDocumentListInput';
import CardIdentityDocumentInput from 'domain/people/employee/shared/input/IdentityDocumentInput/CardIdentityDocumentInput';
import DocumentItem from './DocumentItem';
import FormActionButtons from '../../../shared/button/showEdit/FormActionButtons';
import { IdentityDocument } from './types';
import EditFormShowButton from '../../../shared/button/showEdit/EditFormShowButton';
import { DATE_FORMAT } from 'framework/utils';

const IdentityDocumentsContent: React.FC = () => {
  const { data: employee } = useShowContext();
  const { isGranted } = useAuthorization();
  const { isEditMode } = useFormShowContext();

  if (!isGranted(READ_EMPLOYEE_IDENTITY_DOCUMENTS)) {
    return null;
  }

  const identityDocuments = employee?.getValueBySource('extendedInformation.identityDocuments') || [];

  const formatDate = (date: string) => {
    if (!date) return '';
    try {
      return moment(date).format(DATE_FORMAT.SIMPLE);
    } catch {
      return date;
    }
  };

  const getEditActions = () => {
    if (isEditMode) {
      return (
        <FormActionButtons
          sxWrapper={{
            justifyContent: 'flex-end',
            ml: 0,
          }}
        />
      );
    }
    return <EditFormShowButton requiredPermission={[UPDATE_EMPLOYEE_IDENTITY_DOCUMENTS]} />;
  };

  const renderEditMode = () => (
    <FormSection
      title=""
      formStyle={{
        padding: '0',
        border: 'none',
        boxShadow: 'none',
        outline: 'none',
        backgroundColor: 'transparent',
      }}
    >
      <CardIdentityDocumentListInput
        source="extendedInformation.identityDocuments"
        inputTemplate={CardIdentityDocumentInput}
      />
    </FormSection>
  );

  const renderEmptyState = () => (
    <Box display="flex" alignItems="center" gap={2}>
      <Typography variant="body1" color="textSecondary">
        No identity documents available
      </Typography>
    </Box>
  );

  const renderDocumentsList = () => (
    <Box display="flex" flexDirection="column" gap={3}>
      {identityDocuments.map((document: IdentityDocument) => {
        return <DocumentItem key={document.id} document={document} formatDate={formatDate} />;
      })}
    </Box>
  );

  const showSectionProps = {
    sx: {
      position: 'relative',
      '.show-title-container': {
        marginBottom: '20px',
      },
    } as const,
    title: (
      <Badge
        sx={{
          color: 'text.primary',
        }}
      />
    ),
    actions: (
      <Box
        sx={{
          position: 'absolute',
          right: '-15px',
          top: '-4px',
          button: {
            m: 0,
          },
        }}
      >
        {getEditActions()}
      </Box>
    ),
    disableDefaultPadding: true,
  };

  return (
    <ShowSection {...showSectionProps}>
      {isEditMode
        ? renderEditMode()
        : !identityDocuments || identityDocuments.length === 0
          ? renderEmptyState()
          : renderDocumentsList()}
    </ShowSection>
  );
};

export default IdentityDocumentsContent;
